import { useEffect, useState } from "react";
import { sessionNeedsSignIn } from "./session-status.js";
import { clearGpsDraft, selectGpsDraftAccount } from "../gps/gps-draft.js";
import {
  isRoundOpen,
  type RoundState,
} from "../../domain/round/round-types.js";
import { authClient } from "../auth/auth-client.js";
import { AuthScreen } from "../auth/auth-screen.js";
import { PrivacyPage } from "../pages/more/privacy-page.js";
import { deviceAccountCache, roundJournal } from "../data/local-database.js";
import { AppShell } from "../shell/app-shell.js";
import { LoadingScreen } from "../shell/loading-screen.js";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useRoundWorkspace } from "./use-round-workspace.js";
import { useProductRoute } from "./product-router.js";
import {
  clearProductPageCache,
  prefetchProductPages,
  selectProductPageAccount,
  useProductPage,
} from "./use-product-page.js";
import { usePwaStatus } from "../pwa/use-pwa-status.js";
import { InstallInvitation } from "../pwa/install-invitation.js";
import { useDeviceRecovery } from "./use-device-recovery.js";
import { hasAuthenticationLink, hasInviteLink } from "../lib/invite-link.js";
import { clearSeasonRoundIntent } from "../sync/season-round-start-store.js";
import {
  clearGameBookImportIntent,
  GameBookImportSession,
} from "../pages/tee-off/ggb/ggb-import-session.js";

export function App(): React.JSX.Element {
  if (window.location.pathname.replace(/\/$/, "") === "/privacy")
    return <PrivacyPage />;
  return (
    <>
      <AccountApp />
      <InstallInvitation />
    </>
  );
}

function AccountApp(): React.JSX.Element {
  const session = authClient.useSession();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const reconnect = () => {
      setOnline(true);
      void session.refetch();
    };
    const offline = () => setOnline(false);
    window.addEventListener("online", reconnect);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", reconnect);
      window.removeEventListener("offline", offline);
    };
  }, [session.refetch]);
  const requiresAuthentication = sessionNeedsSignIn({
    hasUser: Boolean(session.data?.user),
    pending: session.isPending,
    online,
    errorStatus: session.error ? session.error.status : null,
  });
  selectProductPageAccount(session.data?.user.id ?? null);
  const [showRecoveryAuth, setShowRecoveryAuth] = useState(false);
  const [continueRecovery, setContinueRecovery] = useState(false);
  const device = useDeviceRecovery(session.data?.user.id);
  if (session.isPending || device.loading) {
    return <LoadingScreen label="Opening your clubhouse…" />;
  }
  const recovery = device.recovery;
  if (new URLSearchParams(window.location.search).get("token"))
    return <AuthScreen />;
  if (session.data?.user) {
    if (recovery && recovery.account.accountId !== session.data.user.id) {
      return <AccountSwitchBlocked recovery={recovery} />;
    }
    return (
      <SignedInApp
        key={session.data.user.id}
        accountId={session.data.user.id}
        authenticated={true}
      />
    );
  }
  if (!recovery) return <AuthScreen />;
  // An invite, claim or result link needs the account: the local-only shell would
  // redirect to the Clubhouse and drop it.
  if (
    showRecoveryAuth ||
    (!continueRecovery && hasAuthenticationLink(window.location.search))
  ) {
    return (
      <AuthScreen
        recoveryEmail={recovery.account.email}
        onContinueSavedRound={() => {
          setShowRecoveryAuth(false);
          setContinueRecovery(true);
        }}
      />
    );
  }
  return (
    <SignedInApp
      key={recovery.account.accountId}
      accountId={recovery.account.accountId}
      authenticated={false}
      unverifiedSession={!requiresAuthentication}
      onAuthenticate={() => setShowRecoveryAuth(true)}
    />
  );
}

function SignedInApp(props: {
  accountId: string;
  authenticated: boolean;
  unverifiedSession?: boolean;
  onAuthenticate?(): void;
}): React.JSX.Element {
  const { accountId, authenticated } = props;
  const workspace = useRoundWorkspace(accountId, authenticated);
  useGpsDraftLifecycle(accountId, workspace.snapshot?.state);
  const route = useProductRoute();
  // The Rivalry tab lists every rival; the single-rivalry page loads on
  // demand when a rival is opened.
  const dataPage =
    route.page === "rivalry"
      ? "rivals"
      : route.page === "more"
        ? "clubhouse"
        : route.page;
  const page = useProductPage(accountId, dataPage, authenticated);
  const pwa = usePwaStatus(accountId, workspace.snapshot, workspace.queued);
  useEffect(() => {
    if (
      !authenticated &&
      route.page !== "clubhouse" &&
      route.page !== "scorecard"
    ) {
      route.navigate("clubhouse");
    }
  }, [authenticated, route.page]);
  usePrefetchedPages(accountId, Boolean(workspace.bootstrap && page.data));
  usePendingInvite(authenticated, route);

  async function signOut(): Promise<void> {
    const importIntent = new GameBookImportSession(
      localStorage,
      accountId,
    ).read();
    if (
      workspace.pendingSeason ||
      (importIntent && !importIntent.completed) ||
      (await roundJournal.hasUnsynced(accountId))
    ) {
      const blocked =
        "Complete your saved setup or import and confirm the round before signing out.";
      workspace.setMessage(blocked);
      toast(blocked);
      return;
    }
    const result = await authClient.signOut();
    if (result.error) throw new Error("Sign out failed.");
    clearProductPageCache();
    clearSeasonRoundIntent(accountId);
    clearGameBookImportIntent(accountId, localStorage);
    clearGpsDraft();
    await Promise.all([
      roundJournal.clearAccount(accountId),
      deviceAccountCache.remove(accountId),
    ]);
  }

  if (!workspace.bootstrap) {
    return (
      <LoadingScreen
        label={workspace.message || "Every rivalry starts with one round"}
      />
    );
  }
  return (
    <AppShell
      workspace={workspace}
      onSignOut={signOut}
      route={route}
      pageState={page}
      pwaStatus={pwa}
      requiresAuthentication={!authenticated && !props.unverifiedSession}
      authenticated={authenticated}
      onAuthenticate={props.onAuthenticate}
    />
  );
}

function usePendingInvite(
  authenticated: boolean,
  route: ReturnType<typeof useProductRoute>,
): void {
  // Reopen once on authentication, without forcing later navigation back here.
  useEffect(() => {
    if (authenticated && hasInviteLink(window.location.search))
      route.navigate("rivals");
  }, [authenticated]);
}

function usePrefetchedPages(accountId: string, ready: boolean): void {
  useEffect(() => {
    if (ready) {
      // Clubhouse already loads rival/history data. Warm only the next action,
      // once the visible page has finished, instead of competing with startup.
      prefetchProductPages(accountId, ["tee-off"]);
    }
  }, [accountId, ready]);
}

function AccountSwitchBlocked(props: {
  recovery: NonNullable<ReturnType<typeof useDeviceRecovery>["recovery"]>;
}): React.JSX.Element {
  return (
    <main className="fr-loading">
      <section className="fr-dialog w-full text-left">
        <p className="fr-eyebrow">Saved round found</p>
        <h1 className="fr-h2">End the current session first.</h1>
        <p className="fr-lead">
          This device has saved work for {props.recovery.account.displayName}.
          End the current session before you continue.
        </p>
        <Button
          variant="gold"
          size="block"
          onClick={() => void authClient.signOut()}
        >
          End current session
        </Button>
      </section>
    </main>
  );
}

function useGpsDraftLifecycle(
  accountId: string,
  state: RoundState | undefined,
): void {
  useEffect(() => {
    selectGpsDraftAccount(accountId);
    if (state && !isRoundOpen(state)) clearGpsDraft();
  }, [accountId, state]);
}
